import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef, type ReactNode } from 'react';
import type { ResultTable as ResultTableDto } from '@visualitics-toolkit/shared';

import { CopyButton } from '../atoms';
import { JsonExportButton } from '../molecules';

/** Tabela virtualizada: resultados de operação chegam com dezenas de milhares de linhas. */
export function ResultTable({ table }: { table: ResultTableDto }): ReactNode {
  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(
    () =>
      table.columns.map((column) => ({
        id: column,
        header: column,
        accessorFn: (row) => row[column],
        cell: (info) => formatCell(info.getValue()),
      })),
    [table.columns],
  );

  const instance = useReactTable({
    data: table.rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const rows = instance.getRowModel().rows;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 32,
    overscan: 12,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom = virtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-content">
          {table.label}{' '}
          <span className="text-muted">({table.rowCount.toLocaleString('pt-BR')} linhas)</span>
        </h3>
        <div className="flex items-center gap-1">
          <CopyButton value={toTsv(table)} label="Copiar TSV" />
          <JsonExportButton data={table.rows} filename={`${slug(table.label)}.json`} />
        </div>
      </div>

      <div
        ref={containerRef}
        className="max-h-96 overflow-auto rounded-md border border-border"
        role="region"
        aria-label={table.label}
      >
        <table className="w-full border-collapse text-left text-sm">
          <thead className="sticky top-0 z-10 bg-surface text-xs uppercase text-muted">
            {instance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="whitespace-nowrap px-3 py-2 font-medium">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: paddingTop }} colSpan={table.columns.length} />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              return (
                <tr key={row.id} className="border-t border-border/60">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: paddingBottom }} colSpan={table.columns.length} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function toTsv(table: ResultTableDto): string {
  const header = table.columns.join('\t');
  const body = table.rows
    .map((row) => table.columns.map((column) => formatCell(row[column])).join('\t'))
    .join('\n');
  return `${header}\n${body}`;
}

function slug(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .toLowerCase();
}
